import { useCallback, useMemo } from "react";
import InterventionActionPlan from "@/components/InterventionActionPlan";
import HeroStatusChip from "@/components/HeroStatusChip";
import { useMistralNarrativeOverlay } from "@/hooks/useMistralNarrativeOverlay";
import { cardsToInsightLines } from "@/utils/insightActionPlanDefaults";
import {
  bulletsToActionQueue,
  bulletsToRootCauseCards,
} from "@/utils/mistralInsightMappers";
import {
  AlertTriangle,
  BarChart3,
  Check,
  GraduationCap,
  LayoutGrid,
  Lightbulb,
  Shield,
  ShieldCheck,
} from "lucide-react";
import {
  buildInsightReadinessScore,
  buildInsightGaugeStyle,
  buildInsightHeroNarrative,
  buildInsightPrimaryNote,
  buildInsightHeroKpis,
  buildInsightDomains,
  buildRootCauseSignals,
  buildRecommendationStrength,
  buildInsightWatchlist,
  buildInsightActions,
  buildCTTeacherInsightsMarkdown,
} from "@/utils/ctteacherInsightsDerived";
import { toTitleCase } from "../utils/toTitleCase";

const DOMAIN_ICONS = {
  green: ShieldCheck,
  "": Shield,
  red: AlertTriangle,
  orange: BarChart3,
};

const SIGNAL_ICONS = {
  green: Check,
  red: AlertTriangle,
  orange: GraduationCap,
  purple: LayoutGrid,
};

const ACTION_ICONS = {
  red: AlertTriangle,
  orange: GraduationCap,
  purple: LayoutGrid,
};

const scrollToId = (id) => {
  document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
};

const CTTeacherInsightsTab = ({ overview = {}, blockData = [] }) => {
  const hasData = Boolean(overview?.total_teachers);

  const buildLocalMarkdown = useCallback(
    () => buildCTTeacherInsightsMarkdown(overview, blockData),
    [overview, blockData]
  );
  const scopeKey = useMemo(
    () => JSON.stringify({ t: overview?.total_teachers, c: overview?.ctet_pct }),
    [overview?.total_teachers, overview?.ctet_pct]
  );
  const {
    loading: aiLoading,
    pickFromBucket,
    pickPrimaryAction,
    pickMapped,
    isMistral,
    markdown: aiMarkdown,
  } = useMistralNarrativeOverlay({
    kind: "ctteacher-insights",
    title: "CT Teacher AI Insights",
    buildLocalMarkdown,
    context: { overview, blockData },
    enabled: hasData,
    scopeKey,
  });

  const readinessScore = useMemo(() => buildInsightReadinessScore(overview), [overview]);
  const gaugeStyle = useMemo(() => buildInsightGaugeStyle(readinessScore), [readinessScore]);
  const localHeroNarrative = useMemo(() => buildInsightHeroNarrative(overview), [overview]);
  const heroNarrative = useMemo(
    () => (aiLoading ? localHeroNarrative : pickFromBucket(localHeroNarrative, "insights", 0)),
    [aiLoading, pickFromBucket, localHeroNarrative]
  );
  const localPrimaryNote = useMemo(() => buildInsightPrimaryNote(overview), [overview]);
  const primaryNote = useMemo(
    () => (aiLoading ? localPrimaryNote : pickFromBucket(localPrimaryNote, "insights", 1)),
    [aiLoading, pickFromBucket, localPrimaryNote]
  );
  const heroKpis = useMemo(() => buildInsightHeroKpis(overview), [overview]);
  const domains = useMemo(
    () => buildInsightDomains(overview, blockData),
    [overview, blockData]
  );
  const localSignals = useMemo(
    () => buildRootCauseSignals(overview, blockData),
    [overview, blockData]
  );
  const signals = useMemo(() => {
    const mapped = pickMapped(localSignals, "root", bulletsToRootCauseCards);
    return mapped.map((item, index) => ({
      ...localSignals[index],
      ...item,
      description: item.body || localSignals[index]?.description,
    }));
  }, [pickMapped, localSignals]);
  const recommendations = useMemo(
    () => buildRecommendationStrength(overview),
    [overview]
  );
  const watchlist = useMemo(
    () => buildInsightWatchlist(overview, blockData),
    [overview, blockData]
  );
  const localActions = useMemo(
    () => buildInsightActions(overview, blockData),
    [overview, blockData]
  );
  const actions = useMemo(() => {
    const mapped = pickMapped(localActions, "actions", bulletsToActionQueue);
    return mapped.map((item, index) => ({
      title: item.title,
      description: item.body,
      iconClass: localActions[index]?.iconClass || item.iconClass,
    }));
  }, [pickMapped, localActions]);

  const rootSignalCount = signals.length;
  const priorityActionCount = actions.length;

  const defaultRoot = useMemo(
    () => cardsToInsightLines(localSignals.map((s) => ({ title: s.title, body: s.description }))),
    [localSignals]
  );
  const defaultRecs = useMemo(
    () => recommendations.map((r) => `${String(r.label).replace(/\n/g, " ")}: ${r.value}% strength`),
    [recommendations]
  );
  const defaultActions = useMemo(
    () => cardsToInsightLines(localActions.map((a) => ({ title: a.title, body: a.description }))),
    [localActions]
  );

  return (
    <div className="ctin-insights-tab" data-testid="ctteacher-insights" id="ctin-overview">
      <section className="ctin-hero-card">
        <div className="ctin-hero-layout">
          <div>
            <div className="ctin-insight-main">
              <div
                className="ctin-ai-orb"
                style={gaugeStyle}
                aria-label={`Insight readiness ${readinessScore} percent`}
              >
                <div>
                  <strong>{readinessScore}</strong>
                  <span>
                    Insight
                    <br />
                    Readiness
                  </span>
                </div>
              </div>
              <div className="ctin-hero-copy">
                <h2>AI Insight Summary</h2>
                <p>{heroNarrative}</p>
                <div className="ctin-status-row">
                  <HeroStatusChip chipClass="ctin-status-chip" showChevron={false}>
                    {aiLoading ? "Generating…" : isMistral ? "Mistral AI" : "Rule-based"}
                  </HeroStatusChip>
                  <HeroStatusChip
                    chipClass="ctin-status-chip"
                    variant="blue"
                    onClick={rootSignalCount > 0 ? () => scrollToId("ctin-watchlist") : undefined}
                    ariaLabel={
                      rootSignalCount > 0 ? `${rootSignalCount} root signals — view watchlist` : undefined
                    }
                  >
                    {rootSignalCount} Root Signals
                  </HeroStatusChip>
                  <HeroStatusChip
                    chipClass="ctin-status-chip"
                    variant="blue"
                    onClick={priorityActionCount > 0 ? () => scrollToId("ctin-actions") : undefined}
                    ariaLabel={
                      priorityActionCount > 0
                        ? `${priorityActionCount} priority actions — view action queue`
                        : undefined
                    }
                  >
                    {priorityActionCount} Priority Actions
                  </HeroStatusChip>
                </div>
              </div>
            </div>
            <div className="ctin-summary-note">
              <div className="ctin-icon-box purple">
                <Lightbulb size={22} />
              </div>
              <div>
                <b>Primary insight</b>
                <span>{primaryNote}</span>
              </div>
            </div>
          </div>

          <div className="ctin-metric-grid" aria-label="Insight summary KPIs">
            {heroKpis.map((card) => (
              <article key={card.label} className={`ctin-metric-card ${card.cardClass}`.trim()}>
                <small>{card.label}</small>
                <strong>{card.value}</strong>
                <span>{card.sub}</span>
              </article>
            ))}
          </div>
        </div>
      </section>

      <div className="ctin-section-title">
        <div>
          <h2>Insight Domains</h2>
          <p>
            Key intelligence areas are separated into compact cards so officers can quickly
            identify what is strong, weak, risky and actionable.
          </p>
        </div>
        <button
          type="button"
          className="ctin-section-action"
          onClick={() => scrollToId("ctin-actions")}
        >
          View actions →
        </button>
      </div>

      <section className="ctin-domain-grid">
        {domains.map((domain) => {
          const Icon = DOMAIN_ICONS[domain.iconClass] ?? Shield;
          return (
            <article key={domain.title} className="ctin-domain-card">
              <div className="ctin-domain-head">
                <div className={`ctin-icon-box ${domain.iconClass}`.trim()}>
                  <Icon size={22} />
                </div>
                <span className={`ctin-status-dot ${domain.dotClass}`.trim()} />
              </div>
              <h3>{toTitleCase(domain.title)}</h3>
              <div className="ctin-domain-score">
                <strong>{domain.score}</strong>
                <span>{domain.scoreLabel}</span>
              </div>
              <div className="ctin-progress-line">
                <i style={{ width: `${domain.barPct}%`, background: domain.barColor }} />
              </div>
              <div className="ctin-domain-meta">
                <span>{domain.metaLeft}</span>
                <span>{domain.metaRight}</span>
              </div>
            </article>
          );
        })}
      </section>

      <div className="ctin-section-title">
        <div>
          <h2>Root-Cause Signals and Recommendation Strength</h2>
          <p>
            Instead of a long text accordion, the insight section now shows evidence-backed
            signals, impact levels and action strength in visual panels.
          </p>
        </div>
        <button
          type="button"
          className="ctin-section-action"
          onClick={() => scrollToId("ctin-watchlist")}
        >
          Go to watchlist →
        </button>
      </div>

      <section className="ctin-analytics-grid">
        <article className="ctin-panel">
          <div className="ctin-panel-head">
            <div>
              <h3>Root-Cause Signals</h3>
              <p>
                Signals extracted from data completeness, verification, certification and block
                distribution patterns.
              </p>
            </div>
            <span className="ctin-pill">Top {signals.length}</span>
          </div>
          <div className="ctin-signal-list">
            {signals.map((signal) => {
              const Icon = SIGNAL_ICONS[signal.iconClass] ?? Check;
              return (
                <div key={signal.title} className="ctin-signal-item">
                  <div className={`ctin-icon-box ${signal.iconClass}`.trim()}>
                    <Icon size={22} />
                  </div>
                  <div>
                    <h4>{signal.title}</h4>
                    <p>{signal.description}</p>
                  </div>
                  <div className="ctin-impact-score">{signal.impact}</div>
                </div>
              );
            })}
          </div>
        </article>

        <article className="ctin-panel">
          <div className="ctin-panel-head">
            <div>
              <h3>Recommendation Strength</h3>
              <p>Prioritization score based on urgency, scale and expected improvement.</p>
            </div>
            <span className="ctin-pill">Action weight</span>
          </div>
          <div className="ctin-hbar-list">
            {recommendations.map((row) => (
              <div key={row.label} className="ctin-hbar-row">
                <div className="ctin-hbar-label">{row.label}</div>
                <div className="ctin-hbar-track">
                  <div
                    className={`ctin-hbar ${row.barClass}`.trim()}
                    style={{ width: `${row.value}%` }}
                  />
                </div>
                <div className="ctin-hbar-value">{row.value}</div>
              </div>
            ))}
          </div>
        </article>
      </section>

      <section className="ctin-action-plan">
        <InterventionActionPlan
          aiContent={aiMarkdown}
          defaultRoot={defaultRoot}
          defaultRecs={defaultRecs}
          defaultActions={defaultActions}
        />
      </section>

      <div className="ctin-section-title" id="ctin-watchlist">
        <div>
          <h2>Priority Watchlist and Action Queue</h2>
          <p>
            The bottom section turns AI insights into reviewable officer actions with owners,
            evidence and priority levels.
          </p>
        </div>
        <button
          type="button"
          className="ctin-section-action"
          onClick={() => scrollToId("ctin-overview")}
        >
          Back to top ↑
        </button>
      </div>

      <section className="ctin-two-column" id="ctin-actions">
        <article className="ctin-panel ctin-table-card">
          <div className="ctin-table-head">
            <div>
              <h3>Block Insight Watchlist</h3>
              <p>Blocks highlighted for either strong practice replication or targeted support.</p>
            </div>
            <span className="ctin-pill">Review set</span>
          </div>
          <div className="ctin-table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Priority</th>
                  <th>Block</th>
                  <th>Signal</th>
                  <th>Evidence</th>
                  <th>Recommended Move</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {watchlist.map((row) => (
                  <tr key={`${row.priority}-${row.block}-${row.signal}`}>
                    <td>{row.priority}</td>
                    <td>{row.block}</td>
                    <td>{row.signal}</td>
                    <td className="score">{row.evidence}</td>
                    <td>{row.move}</td>
                    <td>
                      <span className={`ctin-tag ${row.tagClass}`.trim()}>{row.status}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </article>

        <article className="ctin-panel">
          <div className="ctin-panel-head">
            <div>
              <h3>Recommended Action Queue</h3>
              <p>Three high-value actions for the next review cycle.</p>
            </div>
            <span className="ctin-pill">Priority</span>
          </div>
          <div className="ctin-action-card">
            {actions.map((action) => {
              const Icon = ACTION_ICONS[action.iconClass] ?? AlertTriangle;
              return (
                <div key={action.title} className="ctin-action-item">
                  <div className={`ctin-icon-box ${action.iconClass}`.trim()}>
                    <Icon size={22} />
                  </div>
                  <div>
                    <h4>{action.title}</h4>
                    <p>{action.description}</p>
                  </div>
                </div>
              );
            })}
            <div className="ctin-action-footer">
              <b>Next review recommendation</b>
              <span>
                Run a 30-day teacher-quality improvement cycle and track CTET, NISHTHA and block
                staffing exceptions every Friday.
              </span>
            </div>
          </div>
        </article>
      </section>
    </div>
  );
};

export default CTTeacherInsightsTab;
